import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip, LabelList } from 'recharts';

const data = [
  { subject: 'Geopolitical', risk: 10 },
  { subject: 'Macroeconomic', risk: 6 },
  { subject: 'Natural Disaster', risk: 7 },
  { subject: 'Geography', risk: 5 },
  { subject: 'Resilinc Financial Risk', risk: 7 },
  { subject: 'Resilinc Compliance Risk', risk: 7.8 },
  { subject: 'Resilinc ESC Risk', risk: 3.6 },
  { subject: 'Resilinc Infosec Risk', risk: 6.9 },
  { subject: 'ResilincOps Risk', risk: 8.2 },
];

// âœ… FIX: Calculate angles properly based on index
const CustomTick = ({ payload, x, y, cx, cy }) => {
  // console.log(payload);

  const totalLabels = data.length;
  const angleStep = (2 * Math.PI) / totalLabels; // Evenly distribute angles
  const angle = payload.index * angleStep - Math.PI / 2; // Offset by -90 degrees

  const baseRadius = 165; // Default radius
  let adjustedRadius = baseRadius;

  // ðŸš€ Increase radius for specific overlapping subjects
  if (payload.value === "Resilinc Compliance Risk" || payload.value === "Geography") {
     adjustedRadius += 20; // Move label outward
  }

  const newX = cx + adjustedRadius * Math.cos(angle);
  const newY = cy + adjustedRadius * Math.sin(angle);

  const words = payload.value.split(" ");

  return (
    <text x={newX} y={newY} textAnchor="middle" fontSize={14} fill="#616161">
      {words.map((word, index) => (
        <tspan key={index} x={newX} dy={14}>
          {word}
        </tspan>
      ))}
    </text>
  );
};

const renderCustomLabel = ({ x, y, value, viewBox, riskColors = () => { } }) => {
  // console.log(viewBox, x, y);

  return (
    <g>
      {/* Background Rectangle */}
      <rect
        x={x - 15}
        y={y - 12}
        width={30}
        height={20}
        rx={5}
        fill="rgba(255, 255, 255, 1)"
      />
      {/* Text */}
      <text x={x} y={y} fill={riskColors(value)} fontSize={12} fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
        {value}
      </text>
    </g>
  )
};

// Splits a label into multiple short lines so long category names (e.g.
// "Reputation & environmental, social and governance (ESG) hotspot") wrap
// onto several rows instead of overlapping neighboring labels around the
// radar's circumference.
const wrapLabelText = (text, maxCharsPerLine = 14) => {
  const words = String(text ?? "").split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach(word => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (candidate.length > maxCharsPerLine && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = candidate;
    }
  });
  if (currentLine) lines.push(currentLine);

  return lines;
};

// Compact, wrapping tick renderer used by the "isSimple" radar (SmartCube's
// Category Risk widget). Unlike CustomTick above, this keeps recharts'
// own computed x/y/anchor (no manual angle re-calculation) and simply
// wraps long labels across multiple lines at a smaller font size so they
// don't overlap adjacent labels, particularly near the top/bottom vertices.
const SimpleAngleTick = ({ x, y, cx, cy, payload }) => {
  const lines = wrapLabelText(payload?.value, 14);
  const textAnchor = Math.abs(x - cx) < 1 ? "middle" : x > cx ? "start" : "end";
  const isBottomHalf = y > cy;
  // When the label sits in the bottom half of the chart, start drawing
  // above the anchor point so the wrapped lines grow downward without
  // creeping further away from the plot; top-half labels grow upward.
  const firstLineDy = isBottomHalf ? 10 : -((lines.length - 1) * 11);

  return (
    <text x={x} y={y} textAnchor={textAnchor} fontSize={10} fill="#616161">
      {lines.map((line, index) => (
        <tspan key={index} x={x} dy={index === 0 ? firstLineDy : 11}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

const RadarChartComponent = ({ data, riskColors, isSimple = false }) => {
  const maxRisk = Math.ceil(Math.max(...data.map(item => item.risk)));

  // Simple/plain hexagon-style radar (no custom rotated ticks, no value
  // labels on the vertices, pink filled area) - used when isSimple is true.
  if (isSimple) {
    return (
      <>
      <ResponsiveContainer width="100%" height={370}>
        <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
          <PolarGrid stroke="#DADADA" />
          <PolarAngleAxis dataKey="subject" tick={<SimpleAngleTick />} />
          <PolarRadiusAxis
            angle={30}
            domain={[0, maxRisk]}
            ticks={Array.from({ length: maxRisk+2 }, (_, i) => i)}
            tick={{ fontSize: 12, fontWeight: "400", fill: "#4B4B4B" }}
          />
          <Radar name="Risk Scores" dataKey="risk" stroke="#C85291" fill="#C85291" fillOpacity={0.5} strokeWidth={2} />
          <Tooltip />
        </RadarChart>
      </ResponsiveContainer>
      
      {/* Custom Legend */}
      <div style={{ display: "flex", justifyContent: "flex-start", padding: "8px 16px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#C85291",
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 12, color: "#4B4B4B", fontFamily: "Inter", whiteSpace: "normal" }}>
            Risk level
          </span>
        </div>
      </div>
      </>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={370}>
      <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data} margin={{ top: 20, right: 0, left: 0, bottom: 0 }}>
        <PolarGrid />
        <PolarAngleAxis dataKey="subject" tick={<CustomTick />} />
        <PolarRadiusAxis
          angle={30}
          domain={[0, maxRisk]}
          ticks={Array.from({ length: maxRisk + 2 }, (_, i) => i)}
          tick={{ fontSize: 14, fontWeight: '400', fill: '#d5d4d4' }}
        />
        <Radar name="Resilinc Risk Scores" dataKey="risk" stroke="#33ABDA" fill="transparent" strokeWidth={4}>
          <LabelList dataKey="risk" content={(props) => renderCustomLabel({ ...props, riskColors })} />
        </Radar>
        <Tooltip />
      </RadarChart>
    </ResponsiveContainer>
  );
};

export default RadarChartComponent;
